import {useCallback, useContext, useEffect, useState} from 'react';

import {useCookies} from 'react-cookie';

import {UserContext} from '../contexts/UserContext/UserContext';
import {GetRoutes} from '../lib/api/GET/GetRoutes';
import {AVATAR_COOKIE_ID} from '../lib/constants';
import {ApplicantRoleData, CompanyAdminRoleData} from '../types/User';
import {formatImageUrl} from '../utils/formatImageUrl';

import {useApiRead} from './useApiRead';

interface ApplicantPicture {
  applicant: {
    applicantProfile: {
      profilePictureUrl: string | null;
    };
  };
}

interface CompanyPicture {
  company: {
    logoUrl?: string | null;
  };
}

export const useUserAvatar = () => {
  const {currentUser} = useContext(UserContext);
  const applicantId = (currentUser?.role?.data as ApplicantRoleData)?.applicantId;
  const companyId = (currentUser?.role?.data as CompanyAdminRoleData)?.companyId;

  const [shouldSetUrlRemotely, setShouldSetUrlRemotely] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string>('');

  const [cookies, setCookie, removeCookie] = useCookies([AVATAR_COOKIE_ID]);

  const {data: applicant, refetch: fetchApplicant} = useApiRead<ApplicantPicture>({
    path: GetRoutes.applicantProfile(applicantId),
    options: {enabled: false},
  });

  const {data: company, refetch: fetchCompany} = useApiRead<CompanyPicture>({
    path: GetRoutes.company(companyId),
    options: {enabled: false},
  });

  const storeUrl = useCallback(
    (url: string) => {
      setCookie(AVATAR_COOKIE_ID, formatImageUrl(url), {
        path: '/',
        domain: process.env.REACT_APP_COOKIE_DOMAIN,
        httpOnly: false,
      });
      setAvatarUrl(formatImageUrl(url) || '');
    },
    [setCookie, setAvatarUrl]
  );

  const updateAvatarUrl = useCallback(
    (url: string) => {
      removeCookie(AVATAR_COOKIE_ID, {
        path: '/',
        domain: process.env.REACT_APP_COOKIE_DOMAIN,
        httpOnly: false,
      });
      storeUrl(url);
    },
    [removeCookie, storeUrl]
  );

  useEffect(() => {
    const fetchUrlRemotely = () => {
      if (applicantId) {
        fetchApplicant();
      }
      if (companyId) {
        fetchCompany();
      }
    };
    const cookie: string | null = cookies[AVATAR_COOKIE_ID];
    if (cookie || cookie === '') {
      setAvatarUrl(cookie);
    } else {
      setShouldSetUrlRemotely(true);
      fetchUrlRemotely();
    }
  }, [cookies, fetchApplicant, fetchCompany, applicantId, companyId]);

  useEffect(() => {
    const shouldSetApplicantUrl = shouldSetUrlRemotely && applicant && applicantId;
    const shouldSetCompanyUrl = shouldSetUrlRemotely && companyId && company;

    if (shouldSetApplicantUrl) {
      storeUrl(applicant?.applicant?.applicantProfile?.profilePictureUrl || '');
    }
    if (shouldSetCompanyUrl) {
      storeUrl(company?.company?.logoUrl || '');
    }
  }, [applicant, company, applicantId, companyId, shouldSetUrlRemotely, storeUrl]);

  return {
    avatarUrl,
    updateAvatarUrl,
  };
};
